import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CryptoJS from "crypto-js";
import { getToken } from "../../../Compo/utilis/getToken";
import PublicPage from "../../PublicPage";
import DsLogo from "./DsLogo";
import { LoginDialog } from "../auth/LoginDialog";

// Landing for the seller's QR code (/Buyer/:encrypted). The QR carries the
// encrypted { contractId, sellerUserId }; it is decrypted and stored under the
// same "data" localStorage key as before. The buyer already knows the deal
// (the QR carries it), so there's no registration-number step to go through —
// they land directly on /BuyerProfile's step 2, the same "waiting for the
// seller to confirm" screen a buyer who typed in the reg number sees.
//   - buyer already signed in  -> straight to the waiting screen
//   - buyer signed out         -> log in (or create an account) here, then the waiting screen
export default function BuyerInviteFlow() {
  const { encrypted } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const readInviteContractId = () => {
    try {
      return JSON.parse(localStorage.getItem("data") || "null")?.contractId ?? null;
    } catch (_) {
      return null;
    }
  };

  const waitingPageState = () => ({ startStep: 2, inviteContractId: readInviteContractId() });

  const goToWaitingPage = () => {
    navigate("/BuyerProfile", { replace: true, state: waitingPageState() });
  };

  // A brand-new email/password account has only a stub name (their email
  // address) and no phone number — route them through the existing
  // first-time-signup onboarding page (/CompleteProfile, CompleteProfileForm)
  // to fill those in first. It reads state.continueTo/continueState and, once
  // the form is saved, continues on to the same waiting screen instead of its
  // normal postLoginRedirect(). BankID signups skip this: BankID's own
  // in-dialog registration form already collects email/phone/password before
  // it ever calls afterLogin.
  const goToProfileThenWaiting = () => {
    navigate("/CompleteProfile", {
      replace: true,
      state: { continueTo: "/BuyerProfile", continueState: waitingPageState() },
    });
  };

  useEffect(() => {
    if (!encrypted) {
      // Bare /Buyer: no invitation to resolve; an existing "data" entry (from an
      // earlier scan) still works for a signed-in buyer.
      if (getToken() && localStorage.getItem("data")) goToWaitingPage();
      return;
    }

    let saved = false;
    try {
      const passphrase = "Klargo3613";
      const decoded = decodeURIComponent(encrypted);

      const bytes = CryptoJS.AES.decrypt(decoded, passphrase);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedText) {
        setInvalid(true);
        return;
      }

      const data = JSON.parse(decryptedText);

      if (data.contractId && data.sellerUserId) {
        localStorage.setItem(
          "data",
          JSON.stringify({
            contractId: data.contractId,
            sellerUserId: data.sellerUserId,
          }),
        );
        saved = true;
      }
    } catch (err) {
      console.error("Decrypt error:", err);
      setInvalid(true);
      return;
    }

    if (saved && getToken()) {
      goToWaitingPage();
    }
  }, [encrypted, navigate]);

  const signedIn = !!getToken();

  return (
    <PublicPage>
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <header className="bg-ds-ink text-ds-ink-on-soft" style={{ boxShadow: "0 1px 0 rgba(0,0,0,.25)" }}>
          <div className="mx-auto flex h-14 max-w-[1200px] items-center px-5 md:px-8">
            <span aria-label="Klargo" className="flex items-center gap-2 px-0.5 py-3">
              <DsLogo />
            </span>
          </div>
        </header>

        <main className="ds-scope flex w-full flex-1 items-start justify-center px-4 pb-16 pt-10 md:px-6 md:pt-16">
          <div className="ds-form w-full max-w-xl">
            {invalid ? (
              <div role="alert" className="ds-panel-note">
                <h1 className="text-lg font-semibold text-ds-ink">{t("buyer.invite.invalidTitle")}</h1>
                <p className="mt-1 text-sm leading-relaxed text-ds-text">{t("buyer.invite.invalidBody")}</p>
              </div>
            ) : signedIn ? (
              <div className="flex justify-center py-12" role="status" aria-label={t("buyerauth.step0.loading")}>
                <div
                  className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
                  style={{ borderTopColor: "var(--ds-green)" }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                <div>
                  <h1 className="ds-title-step">{t("buyer.invite.title")}</h1>
                  <p className="ds-body mt-1.5 text-ds-muted">{t("buyer.invite.body")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setLoginOpen(true)}
                  className="ds-action-ink w-full justify-center !py-3"
                >
                  {t("buyer.invite.cta")}
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* After login (or account creation) the buyer lands directly on the
          "waiting for the seller to confirm" screen — the same one a buyer
          who typed in the registration number sees. */}
      <LoginDialog
        open={loginOpen}
        onOpenChange={setLoginOpen}
        intent="buyer"
        resumePending
        bankIdScope="invite"
        afterLogin={({ isNewSignup } = {}) => (isNewSignup ? goToProfileThenWaiting() : goToWaitingPage())}
      />
    </PublicPage>
  );
}
